import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePortalAccess } from '../hooks/usePortalAccess';

/**
 * When portal is locked, only allow access to a short list of routes (e.g. Dashboard, Billing, Roadmap).
 * Redirects locked users to /portal/billing for any other route.
 */
const ALLOWED_WHEN_LOCKED = [
  '/portal',
  '/portal/billing',
  '/portal/roadmap',
  '/portal/profile',
  '/portal/checkout',
  '/portal/checkout/return',
  '/portal/checkout/strategy-calls/return',
  '/portal/programdetail',
  '/portal/customprogram',
  '/portal/contract',
];

const isPathAllowedWhenLocked = (pathname) => {
  if (ALLOWED_WHEN_LOCKED.includes(pathname)) return true;
  // Allow programdetail with query params
  if (pathname.startsWith('/portal/programdetail')) return true;
  if (pathname.startsWith('/portal/roadmap')) return true;
  if (pathname.startsWith('/portal/customprogram')) return true;
  if (pathname.startsWith('/portal/billing')) return true;
  if (pathname.startsWith('/portal/contract')) return true;
  return false;
};

const PortalLockGuard = ({ children }) => {
  const { isPortalLocked, isLockedPageHidden, loading } = usePortalAccess();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
      </div>
    );
  }

  if (isPortalLocked) {
    if (!isPathAllowedWhenLocked(location.pathname)) {
      return <Navigate to="/portal/billing" replace />;
    }
    // Roadmap is the only allowed-when-locked route that also has an Access tab
    // toggle; an explicit off there beats the allowlist.
    if (
      location.pathname.startsWith('/portal/roadmap') &&
      isLockedPageHidden('/portal/roadmap')
    ) {
      return <Navigate to="/portal/billing" replace />;
    }
  }

  return <>{children}</>;
};

export default PortalLockGuard;
